import React, { useEffect, useRef, useState } from "react";
import Plotly from "plotly.js-dist-min";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface HourlyChartProps {
    location_id: number;
    timezone: string;
    threshold: number;
}

const HourlyChart: React.FC<HourlyChartProps> = ({ location_id, timezone, threshold }) => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
    const plotRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const plotAttempts = useRef(0);

    // New state to hold the entire 30-day dataset.
    const [dataSet, setDataSet] = useState<any[]>([]);

    // Helper: round a Date down to the nearest hour.
    const roundDownToHour = (date: Date): Date => {
        const d = new Date(date);
        d.setMinutes(0, 0, 0);
        return d;
    };

    // Helper: format date as MM/DD/YY, HH (24-hour format)
    const formatDate = (date: Date): string => {
        const options: Intl.DateTimeFormatOptions = {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
            hour: "numeric", // single-digit hour if applicable
            hour12: true     // shows AM/PM
        };
        return new Intl.DateTimeFormat("en-US", options).format(date);
    };


    // State for the visible 24-hour window, initially rounded.
    const [visibleWindow, setVisibleWindow] = useState<{ start: Date; end: Date }>(() => {
        const now = roundDownToHour(new Date());
        return {
            start: new Date(now.getTime() - 24 * 60 * 60 * 1000),
            end: now,
        };
    });

    // Fetch data from your API.
    const fetchData = async (startTime: string, endTime: string, API_BASE_URL: string) => {

        const url = `${API_BASE_URL}/db/leq_values?start_time=${encodeURIComponent(
            startTime
        )}&end_time=${encodeURIComponent(
            endTime
        )}&location_id=${encodeURIComponent(location_id)}&frequency_band=dBA&time_increment=hourly`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error: ${response.status}`);
        }
        const data = await response.json();

        return data;
    };

    // Render the plot using only the data that falls within the visible window.
    const renderPlot = async () => {
        try {
            if (!plotRef.current) {
                if (plotAttempts.current > 10) {
                    throw new Error("Failed to initialize plot after multiple attempts");
                }
                plotAttempts.current += 1;
                setTimeout(renderPlot, 200);
                return;
            }
            setLoading(true);

            // Filter the dataset to only include data points within the visible window.
            const filteredData = dataSet.filter((d: any) => {
                const dTime = new Date(d.time);
                return dTime >= visibleWindow.start && dTime <= visibleWindow.end;
            });

            if (!filteredData || filteredData.length === 0) {
                throw new Error("No data available for the selected time range");
            }

            // Convert times using the specified timezone.
            const times = filteredData.map((d: any) => {
                const utcDate = new Date(d.time);
                return new Date(utcDate.toLocaleString("en-US", { timeZone: timezone }));
            });
            const levels = filteredData.map((d: any) => d.leq_value);
            const colors = levels.map((level: number) =>
                level > threshold ? "red" : "gray"
            );

            const trace: Partial<Plotly.PlotData> = {
                x: times,
                y: levels,
                type: "bar" as const,
                marker: { color: colors },
                hoverlabel: {
                    bgcolor: "dark grey",
                    font: { color: "white" }
                }
            };

            const thresholdLine: Partial<Plotly.Shape> = {
                type: "line" as const,
                x0: times[0],
                x1: times[times.length - 1],
                y0: threshold,
                y1: threshold,
                line: { color: "black", width: 2, dash: "dash" }
            };

            const layout: Partial<Plotly.Layout> = {
                title: "Hourly Sound Pressure Levels",
                margin: { t: 10, b: 30, l: 40, r: 20 },
                xaxis: {
                    title: "Time",
                    rangeslider: {
                        visible: false,
                        borderwidth: 0,
                        thickness: 0.1
                    },
                    type: "date" as const,
                    tickformat: "%I:%M %p",
                    hoverformat: "%I:%M %p %B %d"
                },
                yaxis: { title: "Sound Level (dB)" },
                shapes: [thresholdLine],
                height: 200,
                autosize: true,
                plot_bgcolor: "#ffffff"
            };

            await Plotly.newPlot(
                plotRef.current,
                [trace],
                layout,
                {
                    displayModeBar: false,
                    responsive: true,
                    scrollZoom: false
                }
            );
            setError(null);
            setLoading(false);
        } catch (err: any) {

            setError("Failed to load chart data");
            setLoading(false);
        }
    };

    // When the component mounts (or location changes), fetch the full 30-day dataset.
    useEffect(() => {
        let mounted = true;
        const loadData = async () => {
            try {
                const endTime = new Date();
                const startTime = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                const data = await fetchData(startTime.toISOString(), endTime.toISOString(), API_BASE_URL);
                if (mounted) {
                    setDataSet(data);
                    // Set the initial visible window to the most recent 24 hours, rounded.
                    const roundedNow = roundDownToHour(new Date());
                    setVisibleWindow({
                        start: new Date(roundedNow.getTime() - 24 * 60 * 60 * 1000),
                        end: roundedNow,
                    });
                }
            } catch (err: any) {

                if (mounted) setError("Failed to load data");
            }
        };
        loadData();
        return () => {
            mounted = false;
        };
    }, [location_id, API_BASE_URL]);

    // Re-render the plot whenever the data or visible window changes.
    useEffect(() => {
        if (dataSet.length > 0) {
            renderPlot();
        }
    }, [dataSet, visibleWindow, timezone, threshold]);

    // Navigation: shift the visible window by 6 hours, ensuring the window remains rounded.
    const handleBack = () => {
        setVisibleWindow(prev => {
            const shift = 6 * 60 * 60 * 1000;
            const newStart = roundDownToHour(new Date(prev.start.getTime() - shift));
            const newEnd = roundDownToHour(new Date(prev.end.getTime() - shift));
            return { start: newStart, end: newEnd };
        });
    };

    const handleForward = () => {
        setVisibleWindow(prev => {
            const shift = 6 * 60 * 60 * 1000;
            const newStart = roundDownToHour(new Date(prev.start.getTime() + shift));
            const newEnd = roundDownToHour(new Date(prev.end.getTime() + shift));
            // Prevent moving forward past the current time.
            const now = roundDownToHour(new Date());
            if (newEnd > now) {
                return { start: new Date(now.getTime() - 24 * 60 * 60 * 1000), end: now };
            }
            return { start: newStart, end: newEnd };
        });
    };

    // Determine if the visible window equals the "most recent" 24 hours.
    const nowRounded = roundDownToHour(new Date());
    const mostRecentStart = new Date(nowRounded.getTime() - 24 * 60 * 60 * 1000);
    const isMostRecent =
        visibleWindow.start.getTime() === mostRecentStart.getTime() &&
        visibleWindow.end.getTime() === nowRounded.getTime();

    const titleText = isMostRecent
        ? "Most Recent 24 Hours"
        : `${formatDate(visibleWindow.start)} - ${formatDate(visibleWindow.end)}`;

    return (
        <Card>
            <MDBox p={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="h5">
                        {formatDate(visibleWindow.start)}
                    </MDTypography>
                    <MDTypography variant="h5">
                        {isMostRecent ? "Current" : formatDate(visibleWindow.end)}
                    </MDTypography>
                </MDBox>
            </MDBox>
            <MDBox p={1} position="relative">
                <div style={{ position: "relative", width: "100%", height: "200px" }}>
                    <div
                        ref={plotRef}
                        style={{
                            width: "100%",
                            height: "100%",
                            visibility: loading ? "hidden" : "visible"
                        }}
                    />
                    {loading && (
                        <MDBox
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor="rgba(255, 255, 255, 0.8)"
                        >
                            <MDTypography variant="body1">
                                Loading chart data...
                            </MDTypography>
                        </MDBox>
                    )}
                    {error && !loading && (
                        <MDBox
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor="rgba(255, 255, 255, 0.8)"
                        >
                            <MDTypography variant="body1" color="error">
                                {error}
                            </MDTypography>
                        </MDBox>
                    )}
                </div>
                {/* Navigation Arrows */}
                <MDBox
                    position="absolute"
                    top="50%"
                    left={0}
                    sx={{ transform: "translateY(-50%)" }}
                >
                    <IconButton onClick={handleBack}>
                        <ArrowBackIosNewIcon />
                    </IconButton>
                </MDBox>
                <MDBox
                    position="absolute"
                    top="50%"
                    right={0}
                    sx={{ transform: "translateY(-50%)" }}
                >
                    <IconButton onClick={handleForward}>
                        <ArrowForwardIosIcon />
                    </IconButton>
                </MDBox>
            </MDBox>
        </Card>
    );
};

export default HourlyChart;
